// =============================================================================
// useInboundSmsBadge - the Inbound SMS nav count: open conversations waiting
// for a reply plus any with an open safety flag (inbound_sms_counts.badge).
// Refreshes on the conversations realtime channel (RLS applies). Never once
// per render.
//
// Round 7 #2 (Nov 2026): the value lives in the shared shell cache
// (shellCountKey("inbound_sms", practice)), filled by the shell's one read
// (shell_counts) every 60s. Its own 60s setInterval is gone; it kept asking
// in hidden tabs. The realtime nudge still reads inbound_sms_counts at once.
// =============================================================================
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { queryClient } from "../lib/queryClient";
import { privateChannel } from "../lib/realtime/presence";
import { practiceTopic } from "../lib/realtime/presenceCore";
import { fetchCounts } from "../lib/inboundSmsApi";
import { readShellKey, shellCountKey, SHELL_POLL_MS } from "../lib/shellCounts";

const EMPTY = { badge: 0, crisis: 0 };

export function useInboundSmsBadge(enabled, practiceId) {
  const on = !!enabled && !!practiceId;
  const key = shellCountKey("inbound_sms", practiceId);
  const q = useQuery({
    queryKey: key,
    queryFn: () => readShellKey("inbound_sms", fetchCounts),
    enabled: on,
    staleTime: SHELL_POLL_MS,
    retry: 0,
  });
  useEffect(() => {
    // A badge is a hint; keep the last value rather than show an error.
    if (q.error) console.warn("[inbound-sms badge] count failed:", q.error && q.error.message);
  }, [q.error]);

  useEffect(() => {
    if (!on) return undefined;
    let t = null;
    const load = () => fetchCounts()
      .then((c) => queryClient.setQueryData(key, c))
      .catch((e) => console.warn("[inbound-sms badge] count failed:", e && e.message));
    const ch = privateChannel(practiceTopic(practiceId, "inbound-sms-badge"))
      .on("postgres_changes", { event: "*", schema: "public", table: "inbound_sms_conversations", filter: "practice_id=eq." + practiceId }, () => {
        clearTimeout(t);
        t = setTimeout(load, 800);
      })
      .subscribe();
    return () => { clearTimeout(t); supabase.removeChannel(ch); };
  }, [on, practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!on || !q.data) return EMPTY;
  return { badge: Number(q.data.badge) || 0, crisis: Number(q.data.crisis) || 0 };
}
